import { PlusOutlined } from "@ant-design/icons";
import { couponApi } from "@api";
import {
  ComponentPagination,
  SearchFilter,
  SelectedFilter,
  SharedButton,
  StyledListPage,
  ComponentHeader,
  StyledInnerListPage,
  SharedIconButton,
  PlusIcon,
  DownloadV2,
} from "@components";

import { cdicCouponFilter, enumFilter, initPage, PATH_GENERATE_CDIC_COUPON } from "@configs";
import { ICdicCoupon, IFormExportValues, IPagination } from "@interfaces";
import { CdicCouponTable } from "@modules";
import { selectApp, setLoading } from "@redux";
import { useNotify } from "@utils";
import { Col, Row, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { ExportSidebarCdic } from "./ExportCdic";

export const PageListCdicCoupon = () => {
  // hook
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { success, error } = useNotify();
  const { mobile } = useSelector(selectApp);
  // state
  const [page, setPage] = useState<IPagination>(initPage);
  const [total, setTotal] = useState<number>(0);
  const [data, setData] = useState<ICdicCoupon[]>([]);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState<string>("");
  const [date, setDate] = useState<string[]>([]);
  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    fetchCoupon();
  }, [id, page, text, date]);

  const fetchCoupon = async () => {
    try {
      dispatch(setLoading(true));
      const res = await couponApi.cdicCoupons(id, {
        page: page.page,
        limit: page.limit,
        searchColumn: filters,
        search: text,
        startDate: date[0],
        endDate: date[1],
      });
      setData(res.data?.data);
      setTotal(res.data?.nTotal);
      success(t("message.fetch.success"));
    } catch (err: any) {
      error(t("message.fetch.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };
  const handlePagination = (current: number, size: number) => {
    setPage({ ...page, page: current, limit: size });
  };

  const goToCreate = () => history.push(PATH_GENERATE_CDIC_COUPON);
  const changeFilter = (value: string) => {
    setText("");
    setFilters(value);
  };
  const searchText = (value: string) => setText(value);
  const handleClearSearch = () => {
    setText("");
    setFilters("");
  };
  const handleDownloadFilter = async (payload: IFormExportValues) => {
    try {
      const { data } = await couponApi.exportStatusCdicExcel(id, {
        active: payload.status?.includes('Active') ? 'active' : '',
        expired: payload.status?.includes('Inactive') ? 'inactive' : '',
        redeemed: payload.status?.includes('Redeemed') ? 'redeemed' : '',
        startDate: payload.date?.[0]
          ? typeof payload.date[0] === "string"
            ? payload.date[0]
            : payload.date[0].format("YYYY-MM-DD")
          : "",
        endDate: payload.date?.[1]
          ? typeof payload.date[1] === "string"
            ? payload.date[1]
            : payload.date[1].format("YYYY-MM-DD")
          : "",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: `text/csv` }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "export redeem coupon");
      link.click();
      window.URL.revokeObjectURL(url);
      success(t("message.download.success"));
    } catch (err: any) {
      error(t("message.download.fail"));
      throw new Error(err);
    }
  };
  const button = (
    <>
      {mobile ? (
        <Tooltip title={t("page.add_new_coupon_family")}>
          <SharedIconButton type="primary" ghost icon={<PlusIcon />} onClick={goToCreate} />
        </Tooltip>
      ) : (
        <Row gutter={8}>
          <Col>
            <Tooltip title={t("page.export_data_by_filter")}>
              <ExportSidebarCdic data={data} handleExport={(params: IFormExportValues)=> handleDownloadFilter(params)} />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title={t("page.download_excel")}>
              <SharedButton
                type="primary"        
                icon={<DownloadV2 />}
                size="middle"
                onClick={()=> handleDownload()}
                style={{background: "#00AA67", color:"#fff"}}
                >
                {t("page.download_coupons")}
              </SharedButton>
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title={t("page.add_cf")}>
              <SharedButton
                type="primary"
                ghost
                icon={<PlusOutlined />}
                size="middle"
                onClick={goToCreate}
              >
                {t("page.add_cf")}
              </SharedButton>
            </Tooltip>
          </Col>
        </Row>
      )}
    </>
  );
  const handleDownload = async () => {
    try {
      const { data } = await couponApi.exportActiveCdicExcel(id, {
        searchColumn: filters,
        search: text,
        startDate: date[0],
        endDate: date[1],
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: `text/csv` }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "export redeem coupon");
      link.click();
      window.URL.revokeObjectURL(url);
      success(t("message.download.success"));
    } catch (err: any) {
      error(t("message.download.fail"));
      throw new Error(err);
    }
  };

  return (
    <>
      <ComponentHeader title={t("page.cdic_coupon_management")} />
      <StyledListPage>
        <StyledInnerListPage>
          <SearchFilter
            actionBtn={button}
            filter={cdicCouponFilter}
            callbackFun={changeFilter}
            searchText={searchText}
            searchInputPlaceholder={t("page.search_user_by")}
            selectedOption={filters}
          />
          <SelectedFilter
            handleClearSearch={handleClearSearch} 
            selected={filters} 
            formDate={date[0]} 
            toDate={date[1]} 
          />
          <CdicCouponTable data={data} callback={fetchCoupon} />
          <ComponentPagination total={total} page={page.page} changePage={handlePagination} />
        </StyledInnerListPage>
      </StyledListPage>
    </>
  );
};
